import { Link } from 'react-router-dom';
import { Archive } from 'lucide-react';
import { cn } from '@/lib/utils';
import { monogram } from '@/lib/client-color';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AvatarPersona } from '@/components/rdt/avatar-persona';
import type { ObraCard as ObraCardData } from '@/types/api';
import { ENC_NOMBRES } from './obras-filtros-tipos';
import { RDT_ETIQUETA_ABREVIADA } from './constants';
import { EstadoObraBadge, EstadoRedesBadge } from './estado-obra-badge';
import { PlacaCc } from './placa-cc';
import { CodigoObraCopiable } from './codigo-obra-copiable';

interface Props {
  obra: ObraCardData;
  /** «Modo archivo» (design_estado_redes_obra.md §1.3): switch «Archivadas»
   *  prendido en la home — muestra el botón «Estado de redes» y atenúa las
   *  cards ya archivadas. */
  modoArchivo?: boolean;
  /** Abre el diálogo «Estado de redes» para este cc — solo se pasa cuando
   *  el usuario puede escribir (gestor/admin, gate en obras.tsx). */
  onEstadoRedes?: (cc: string) => void;
}

function formatFechaAr(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function hoyMedianoche(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// U-10 (rc.6): `RDT_ETIQUETA_ABREVIADA` se mudó a `./constants.ts` (antes
// vivía solo acá) para que `header-obra.tsx` pueda compartirla — el chip RDT
// del header no abreviaba «Esperando Definitiva» como sí lo hacía esta card.

// Tarjeta de obra — variante C cerrada por Juan (2026-07-09): placa del CC
// en color pleno del cliente, SIN barra lateral de acento (border-left
// uniforme de 1px como los otros 3 lados). Los colores de placa (color /
// colorTexto) vienen resueltos del backend y se aplican igual en los dos
// temas; el punto de cliente de la línea 2 también va en color pleno.
//
// Navegación por "stretched link" (review adversarial 2026-07-10): el <a>
// real vive en la denominación y un ::after suyo cubre toda la card — así
// Cmd/Ctrl+clic, clic central y "Abrir en pestaña nueva" funcionan nativo.
// Los controles anidados (copiar, tooltips) van con z-10 por ENCIMA del
// overlay: reciben su hover/click sin disparar la navegación. El anillo de
// foco del link estirado es la ÚNICA excepción permanente al anillo global
// (anillo-unico.test.ts, Ajuste 12 del plan): el `:focus-visible` de base
// solo dibujaría el ancla invisible adentro del título, no toda la card.
//
// R4a (plan_migracion_reui.md §3): EstadoObraBadge/EstadoRedesBadge de R0
// (sobre reui/badge, tal cual); el avatar de Control RDT pasa a
// AvatarPersona (R3, sobre ui/avatar) — separa la capa que recorta (el
// AvatarImage/AvatarFallback, cada uno con su propio rounded-full) de la que
// PUEDE recibir foco (el <span> que arma el propio AvatarPersona, sin
// overflow-hidden: el anillo no queda recortado, Ajuste 39/38 del plan,
// hallazgo del juicio de R0 sobre obra-card.tsx:247-251). Ese <span> NO es
// una parada de Tab por default — Base UI no le inyecta `tabIndex` a un
// `render` no nativamente interactivo — así que acá se pide con
// `enfocable` (hallazgo ALTA de la revisión home-comportamiento, R4a: antes
// de migrar a AvatarPersona este avatar SÍ se alcanzaba por teclado; el
// clipping del anillo y el orden de Tab son dos problemas distintos, y solo
// el primero venía resuelto). El código de obra con su botón de copiar vive en
// CodigoObraCopiable (29/9), compartido con el header de la obra.
export function ObraCardTile({ obra, modoArchivo, onEstadoRedes }: Props) {
  const rdtDisplay = obra.estadoRdt;
  const rdtEtiqueta = rdtDisplay ? RDT_ETIQUETA_ABREVIADA[rdtDisplay] : undefined;

  const dias = obra.fechaFin
    ? Math.round(
        (new Date(`${obra.fechaFin}T00:00:00`).getTime() - hoyMedianoche().getTime()) / 86_400_000,
      )
    : null;
  const semaforoTip =
    dias === null
      ? ''
      : dias < 0
        ? `Fin de contrato: ${formatFechaAr(obra.fechaFin!)} — vencido hace ${-dias} días`
        : `Fin de contrato: ${formatFechaAr(obra.fechaFin!)} — faltan ${dias} días`;

  const nombreEncargada = obra.encargada ? (ENC_NOMBRES[obra.encargada] ?? obra.encargada) : null;

  return (
    <article
      className="group relative flex flex-col gap-[7px] rounded-[11px] border border-border-subtle bg-surface-card pb-3 pl-4 pr-[15px] pt-[13px] shadow-[0_1px_2px_rgba(20,20,22,0.04)] transition-[background-color,border-color,box-shadow] duration-[120ms] ease-out hover:border-border-strong hover:bg-surface-2 hover:shadow-[0_2px_8px_rgba(20,20,22,0.07)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25)] dark:hover:shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
    >
      {/* Head: placa CC + código de obra + logo/monograma */}
      <div className="flex min-w-0 items-center gap-2">
        <PlacaCc cc={obra.cc} color={obra.color} colorTexto={obra.colorTexto} />

        {obra.codigoObra && (
          <CodigoObraCopiable
            codigo={obra.codigoObra}
            className="relative z-10 -my-[3px] py-[3px]"
            textoClassName="max-w-[19ch] text-[11px] text-text-tertiary"
          />
        )}

        <span className="relative z-10 ml-auto flex flex-none items-center gap-1.5 self-center">
          {modoArchivo && onEstadoRedes && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <button
                    type="button"
                    onClick={() => onEstadoRedes?.(obra.cc)}
                    aria-label={`Estado de redes de la obra ${obra.cc}`}
                    className="relative flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[6px] border border-border-strong bg-surface-card text-muted-foreground hover:bg-plate-hover hover:text-foreground [@media(hover:none)]:after:absolute [@media(hover:none)]:after:-inset-[11px] [@media(hover:none)]:after:content-['']"
                  />
                }
              >
                <Archive className="h-[13px] w-[13px]" />
              </TooltipTrigger>
              <TooltipContent>{`Estado de redes · hoy ${obra.estadoRdt ?? 'sin estado'}`}</TooltipContent>
            </Tooltip>
          )}
          {obra.logoUrl ? (
            <span
              aria-hidden="true"
              className="inline-flex h-[22px] max-w-[76px] items-center rounded-[6px] border border-border-subtle bg-white px-[5px]"
            >
              <img src={obra.logoUrl} alt="" className="h-[15px] w-auto max-w-[64px] object-contain" />
            </span>
          ) : (
            <span
              aria-hidden="true"
              className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-surface-2 text-[8.5px] font-bold tracking-[0.02em] text-text-tertiary group-hover:bg-plate-hover"
            >
              {obra.cliente ? monogram(obra.cliente) : ''}
            </span>
          )}
        </span>
      </div>

      {/* Denominación — el link real de la card (stretched: su ::after cubre
          todo el <article>, que es el ancestro relative más cercano).
          Modo archivo + ya archivada (§1.3 del diseño): título y línea 2 un
          poco más apagados, para diferenciarlas de un vistazo del resto. */}
      <h3
        className={cn(
          'm-0 line-clamp-2 text-[13.5px] font-[550] leading-[1.35] tracking-[-0.004em] text-foreground',
          modoArchivo && obra.estadoRdt === 'Archivado' && 'opacity-70',
        )}
      >
        <Link
          to={`/obras/${encodeURIComponent(obra.cc)}`}
          aria-label={`Abrir detalle de la obra ${obra.cc} ${obra.denominacion}`}
          className="after:absolute after:inset-0 after:rounded-[11px] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-offset-2 focus-visible:after:ring-offset-surface-page"
        >
          {obra.denominacion}
        </Link>
      </h3>

      {/* Línea 2: cliente + coordinador (los dos son nullables en el maestro;
          el separador solo aparece si están ambos) */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground',
          modoArchivo && obra.estadoRdt === 'Archivado' && 'opacity-70',
        )}
      >
        <span
          aria-hidden="true"
          className={cn('h-2 w-2 flex-none rounded-full', !obra.color && 'bg-border-strong')}
          style={obra.color ? { background: obra.color } : undefined}
        />
        {obra.cliente && <span>{obra.cliente}</span>}
        {obra.cliente && obra.coordinador && <span className="text-border-strong">·</span>}
        {obra.coordinador && <span>{obra.coordinador}</span>}
      </div>

      {/* Fila inferior: badge estado obra + chip estado RDT + who (encargada/semáforo) */}
      <div className="mt-0.5 flex min-w-0 flex-nowrap items-center gap-1.5 border-t border-border-subtle pt-[9px]">
        <EstadoObraBadge estado={obra.estado ?? 'Sin estado'} />

        {rdtDisplay && (
          // «RDT» ADENTRO de la píldora, como la rc.1 (V2, 28/9: decisión de
          // Juan — antes vivía como texto plano AFUERA del Badge; distingue
          // igual de bien dos badges de estado en la misma fila, "Activo" de
          // obra junto a "Activo" de redes). Con abreviatura (hoy solo
          // «Esperando Definitiva» → «Esp. Definitiva», artboard rdtTxt):
          // Tooltip de la app con el texto completo al hover — el sr-only
          // del propio Badge ya cubre el lector de pantalla
          // (estado-obra-badge.tsx).
          <span className="relative z-10 inline-flex min-w-0 shrink items-center">
            {rdtEtiqueta ? (
              <Tooltip>
                <TooltipTrigger render={<span className="inline-flex min-w-0 shrink" />}>
                  <EstadoRedesBadge estado={rdtDisplay} etiqueta={rdtEtiqueta} prefijo="RDT" className="min-w-0 shrink truncate" />
                </TooltipTrigger>
                <TooltipContent>{rdtDisplay}</TooltipContent>
              </Tooltip>
            ) : (
              <EstadoRedesBadge estado={rdtDisplay} prefijo="RDT" className="min-w-0 shrink truncate" />
            )}
          </span>
        )}

        <span className="ml-auto inline-flex shrink-0 items-center gap-2">
          {obra.encargada && (
            <span className="relative z-10 flex-none">
              <AvatarPersona
                nombre={nombreEncargada!}
                fotoUrl={obra.encargadaFotoUrl}
                size={22}
                tooltip={`${nombreEncargada} — Control RDT`}
                enfocable
                className="border border-border-strong group-hover:bg-plate-hover"
              />
            </span>
          )}

          {dias !== null && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <span
                    tabIndex={0}
                    aria-label={semaforoTip}
                    className="relative z-10 inline-flex items-center p-1"
                  />
                }
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'h-[7px] w-[7px] flex-none rounded-full',
                    dias < 60 ? 'bg-destructive' : dias < 90 ? 'bg-warning opacity-85' : 'bg-success opacity-55',
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>{semaforoTip}</TooltipContent>
            </Tooltip>
          )}
        </span>
      </div>
    </article>
  );
}
